import type { SideBySideRow } from "@productlane/lib/src/prosemirror-diff"

// Unchanged blocks kept either side of a change. Anything beyond this collapses
// into one expandable row, so a two-word fix in a long article stays findable.
const CONTEXT_BLOCKS = 2

export type SplitDiffEntry =
	| { type: "row"; row: SideBySideRow; index: number }
	| { type: "gap"; count: number; key: string }

// Keep `CONTEXT_BLOCKS` unchanged rows on each side of a change and fold the
// rest into one gap. A gap the reviewer already opened stays open.
export function collapseUnchanged(
	rows: readonly SideBySideRow[],
	expandedGaps: ReadonlySet<string>,
): SplitDiffEntry[] {
	const keep = new Set<number>()
	rows.forEach((row, index) => {
		if (row.kind === "equal") return
		for (
			let i = Math.max(0, index - CONTEXT_BLOCKS);
			i <= Math.min(rows.length - 1, index + CONTEXT_BLOCKS);
			i++
		) {
			keep.add(i)
		}
	})

	const entries: SplitDiffEntry[] = []
	let index = 0
	while (index < rows.length) {
		const row = rows[index]
		if (!row) break
		if (keep.has(index)) {
			entries.push({ type: "row", row, index })
			index += 1
			continue
		}
		const start = index
		while (index < rows.length && !keep.has(index)) index += 1
		const key = `gap-${start}-${index}`
		if (expandedGaps.has(key)) {
			for (let i = start; i < index; i++) {
				const hidden = rows[i]
				if (hidden) entries.push({ type: "row", row: hidden, index: i })
			}
		} else {
			entries.push({ type: "gap", count: index - start, key })
		}
	}
	return entries
}

// One section per changed block, with consecutive unchanged blocks merged into
// a single passage. Merging matters: a passage renders its blocks through the
// editor's own typography, so the article keeps its real spacing between
// paragraphs, headings and lists instead of every block sitting in its own box.
export type FlatSection =
	| { kind: "prose"; key: string; html: string; rowIndices: number[] }
	| { kind: "gap"; key: string; count: number }
	| {
			kind: "change"
			key: string
			row: SideBySideRow
			rowIndices: number[]
	  }

export function toFlatSections(
	entries: readonly SplitDiffEntry[],
): FlatSection[] {
	const sections: FlatSection[] = []
	let unchanged: string[] = []
	let unchangedIndices: number[] = []

	const flush = () => {
		if (unchanged.length === 0) return
		sections.push({
			kind: "prose",
			key: `prose-${sections.length}`,
			html: joinBlockHtml(unchanged),
			rowIndices: unchangedIndices,
		})
		unchanged = []
		unchangedIndices = []
	}

	for (const entry of entries) {
		if (entry.type === "gap") {
			flush()
			sections.push({ kind: "gap", key: entry.key, count: entry.count })
			continue
		}
		if (entry.row.kind === "equal") {
			if (entry.row.right?.html) {
				unchanged.push(entry.row.right.html)
				unchangedIndices.push(entry.index)
			}
			continue
		}
		flush()
		sections.push({
			kind: "change",
			key: `change-${entry.index}`,
			row: entry.row,
			rowIndices: [entry.index],
		})
	}
	flush()
	return sections
}

export type DiffSide = "left" | "right"

// One column of the split, flowing on its own. A change the other side owns
// alone contributes nothing here, which is what keeps a run of removals from
// punching a screen-tall hole in the opposite column.
export type ColumnSection =
	| { kind: "prose"; key: string; html: string; rowIndices: number[] }
	| { kind: "gap"; key: string; count: number }
	| {
			kind: "change"
			key: string
			changeId: string
			row: SideBySideRow
			html: string
			rowIndices: number[]
	  }

export function changeIdFor(index: number): string {
	return `chg-${index}`
}

export function toColumnSections(
	entries: readonly SplitDiffEntry[],
	side: DiffSide,
): ColumnSection[] {
	const sections: ColumnSection[] = []
	let unchanged: string[] = []
	let unchangedIndices: number[] = []

	const flush = () => {
		if (unchanged.length === 0) return
		sections.push({
			kind: "prose",
			key: `prose-${side}-${sections.length}`,
			html: joinBlockHtml(unchanged),
			rowIndices: unchangedIndices,
		})
		unchanged = []
		unchangedIndices = []
	}

	for (const entry of entries) {
		if (entry.type === "gap") {
			flush()
			sections.push({ kind: "gap", key: entry.key, count: entry.count })
			continue
		}
		const cell = side === "left" ? entry.row.left : entry.row.right
		if (entry.row.kind === "equal") {
			if (cell?.html) {
				unchanged.push(cell.html)
				unchangedIndices.push(entry.index)
			}
			continue
		}
		if (!cell) continue
		flush()
		sections.push({
			kind: "change",
			key: `change-${side}-${entry.index}`,
			changeId: changeIdFor(entry.index),
			row: entry.row,
			html: cell.html,
			rowIndices: [entry.index],
		})
	}
	flush()
	return sections
}

const VOID_BLOCK =
	/<(img|hr|iframe|video|audio|embed|table|input|svg|canvas)[\s>/]/i

// A divider on its own carries no text, so its change mark has to live on the
// rule itself rather than on a tinted band around it.
export function isRuleOnlyHtml(html: string): boolean {
	return /^(?:\s*<hr\b[^>]*>\s*)+$/i.test(html)
}

// A block the reviewer wiped in the inline editor. Tiptap keeps an empty
// paragraph as the caret target, and writing that back leaves a blank gap in
// the published article.
export function isEmptyBlockHtml(html: string): boolean {
	if (VOID_BLOCK.test(html)) return false
	return (
		html
			.replace(/<[^>]*>/g, "")
			.replace(/&nbsp;|\u00a0/g, " ")
			.trim().length === 0
	)
}

export function applyProposedEdits(
	rows: readonly SideBySideRow[],
	edits: ReadonlyMap<string, { html: string; rowIndices: number[] }>,
): string {
	const overrideByFirstRow = new Map<number, { html: string; last: number }>()
	for (const edit of edits.values()) {
		const first = edit.rowIndices[0]
		const last = edit.rowIndices[edit.rowIndices.length - 1]
		if (first === undefined || last === undefined) continue
		overrideByFirstRow.set(first, { html: edit.html, last })
	}

	const out: string[] = []
	let index = 0
	while (index < rows.length) {
		const override = overrideByFirstRow.get(index)
		if (override) {
			// Nothing pushed, so a wiped list item leaves its neighbours adjacent
			// and they still join back into one list.
			if (!isEmptyBlockHtml(override.html)) out.push(override.html)
			index = override.last + 1
			continue
		}
		out.push(rows[index]?.right?.html ?? "")
		index += 1
	}
	return joinBlockHtml(out)
}

const OPEN_LIST = /^<(ul|ol)((?:\s[^>]*)?)>/
const CLOSE_LIST = /<\/(ul|ol)>$/

// The diff splits a list into one block per item, each in its own `<ul>`/`<ol>`.
// Concatenating them back has to close that seam or the list renders as many.
export function joinBlockHtml(blocks: readonly string[]): string {
	let out = ""
	let open: ListFragment | null = null
	for (const html of blocks) {
		const list = parseListFragment(html)
		if (
			list &&
			open &&
			open.tag === list.tag &&
			open.identity === list.identity
		) {
			out = out.slice(0, -`</${list.tag}>`.length) + html.slice(list.openLength)
		} else {
			out += html
		}
		open = list
	}
	return out
}

interface ListFragment {
	tag: string
	identity: string
	openLength: number
}

function parseListFragment(html: string): ListFragment | null {
	const opening = OPEN_LIST.exec(html)
	const closing = CLOSE_LIST.exec(html)
	const tag = opening?.[1]
	if (!tag || !closing || tag !== closing[1]) return null
	return {
		tag,
		identity: listIdentity(opening[2] ?? ""),
		openLength: opening[0].length,
	}
}

// `start` and the counter offset are per fragment; everything else must match.
function listIdentity(attrs: string): string {
	return attrs
		.trim()
		.split(/\s+(?=[\w-]+=)/)
		.filter((attr) => attr && !/^(start|style)=/.test(attr))
		.sort()
		.join(" ")
}
